import "leaflet/dist/leaflet.css";
import { useEffect, useState } from "react";
import { RxCross2 } from "react-icons/rx";
import baseLayers from "../../constants/baseLayers";
import provinceCenters from "../../constants/provinceCenters";
import useChartData from "../../hooks/useChartData";
import useCsvDownload from "../../hooks/useCsvDownload";
import useGeoJsonLayers from "../../hooks/useGeoJsonLayers";
import useMapInteractions from "../../hooks/useMapInteractions";
import usePointData from "../../hooks/usePointData";
import { appText } from "../../texts";
import getCssVariable from "../../utils/getCssVariable";
import translateWeather from "../../utils/translateWeather";
import ChartRenderer from "../Charts/ChartRenderer";
import "../maps/map.css";
import useMapStore from "../maps/useMapStore";
import SidebarPanel from "../SidebarPanel";
import useMapData from "./Map/Hooks/useMapData";
import LoadingOverlay from "./Map/Loading/Loading";
import MapContainerWrapper from "./Map/MapContainerWrapper";
import OptionCard from "./OptionCard";
const { mapText } = appText;

const MapWithAddMarker = ({ province, isAdmin }) => {
  const { mapRef, setMapRef } = useMapStore() || {
    mapRef: null,
    setMapRef: () => {},
  };
  const { pointData, pointData1, pointData2 } = usePointData(province, isAdmin);
  const [showBanner, setShowBanner] = useState(true);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [showDownloadButton, setShowDownloadButton] = useState(false);
  const [chartKey, setChartKey] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [chartDataList, setChartDataList] = useState(null);

  // slice pointData features to 5 to show in map (todo: fix this)
  //   const newPointData = pointData && {
  //     ...pointData,
  //     features: pointData && pointData.features.slice(0, 5),
  //   }

  const {
    startDate,
    endDate,
    handleStartDateSelect,
    handleEndDateSelect,
    handleGetData,
    hourlyData,
    dailyData,
    mountlyData,
    spi,
    spiSeasonal,
    sunrise,
    sunset,
    selectedLocId,
    setSelectedLocId,
    csvData,
    isLoading,
    errorMessage,
    setErrorMessage,
    hasFetchedDataSuccessfully,
    convertDateToPersian,
    convertDateToPersianWithTime,
    convertToIranTime,
  } = useMapData(selectedFeature, {
    setShowDownloadButton,
    setChartKey,
    setChartDataList,
    setSelectedOptions,
  });
  const [selectedLayer, setSelectedLayer] = useState("osm1");
  const [circleCenter, setCircleCenter] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(6);
  const [tiffType, setTiffType] = useState("Need");
  const [showTiff, setShowTiff] = useState(false);
  const [markerPosition, setMarkerPosition] = useState(null);
  const [isClicked, setIsClicked] = useState(false);
  const selectedSource = selectedFeature?.sourceFile;
  const isSynoptic = selectedSource === "station";
  const ws = hourlyData?.map((d) => d.ws) ?? [];
  const translatedWsData = translateWeather(ws);
  const chartData = useChartData({
    daily: dailyData,
    hourly: hourlyData,
    translatedWsData,
  });

  const handleClick = () => {
    if (isClicked) return;
    resetZoom();
    setIsClicked(true);
    setTimeout(() => {
      setIsClicked(false);
    }, 1000);
  };
  //ساخت مارکر جدید
  const handleAddMarker = (newMarker) => {
    setCurrentMarker(newMarker);
  };

  const persianDates = dailyData.map((data) => convertDateToPersian(data.date));
  const persianDatesHourly = hourlyData.map((data) =>
    convertDateToPersianWithTime(data.date)
  );

  //نمایش دانلود
  const handleOptionChange = (updatedOptions) => {
    setSelectedOptions(updatedOptions);
  };

  const resetZoom = () => {
    if (mapRef) {
      mapRef.setView([32.4279, 53.688], 6);
    }
  };

  const downloadCSV = useCsvDownload(csvData);

  //تغییر لایه های نقشه
  const handleLayerChange = (e) => {
    setSelectedLayer(e.target.value);
  };

  useEffect(() => {
    if (mapRef?.current) {
      mapRef.current.on("zoomend", () => {
        setZoomLevel(mapRef.current.getZoom());
      });
    }
  }, [mapRef]);

  const primaryColor = getCssVariable("--primary-color");
  const secondaryColor = getCssVariable("--secondary-color");
  const tertiaryColor = getCssVariable("--tertiary-color");

  const {
    isMapClickable,
    toggleMapClick,
    isClickActive,
    handleButtonClick,
    points,
    setPoints,
    distance,
    setDistance,
    calculateDistance,
    currentMarker,
    setCurrentMarker,
  } = useMapInteractions(mapRef);

  //دریافت رف نقشه
  useEffect(() => {
    if (currentMarker && mapRef?.current) {
      mapRef.current.setView([currentMarker.lat, currentMarker.lng], 10);
    }
  }, [currentMarker, mapRef]);
  const geoJsonLayerHandlers = useGeoJsonLayers({
    zoomLevel,
    primaryColor,
    secondaryColor,
    tertiaryColor,
    setSelectedFeature,
    setSelectedLocId,
  });

  return (
    <div>
      {showBanner && (
        <div className=' w-full z-[100000] bg-yellow-400 text-black flex justify-between items-center px-4 py-3 shadow-lg font-[myFont]'>
          <span className='text-sm md:text-base'>
            آموزش استفاده از سامانه در بخش «دانلود فایل آموزشی».
          </span>
          <button
            className='text-black text-xl font-bold hover:text-red-600'
            onClick={() => setShowBanner(false)}
          >
            ×
          </button>
        </div>
      )}

      <LoadingOverlay isLoading={isLoading} />
      {errorMessage && (
        <div className='fixed inset-0 bg-black/60 flex justify-center items-center z-[1000000000] text-white'>
          <div className='relative p-10 bg-white rounded-lg text-center shadow-lg shadow-red-200/50'>
            <button
              onClick={() => setErrorMessage("")}
              className='absolute bg-red-100 p-1 rounded-full top-2 right-2 text-red-600 hover:text-red-800'
            >
              <RxCross2 size={24} />
            </button>

            <p className='text-black  lg:text-[24px] text-[16px] mt-2'>
              {errorMessage}
            </p>
          </div>
        </div>
      )}
      <div className='flex mt-4 flex-col lg:flex-row h-[calc(100vh-40px)] gap-x-3'>
        <div className='lg:w-3/12 w-full bg-gray-100 p-4 rounded-lg shadow-lg flex flex-col gap-4 md:overflow-y-auto max-h-[calc(100vh-100px)]'>
          <SidebarPanel
            mapRef={mapRef}
            selectedFeature={selectedFeature}
            onStartDateSelect={handleStartDateSelect}
            onEndDateSelect={handleEndDateSelect}
            handleGetData={handleGetData}
            isLoading={isLoading}
            selectedLocId={selectedLocId}
            startDate={startDate}
            endDate={endDate}
            showDownloadButton={showDownloadButton}
            downloadCSV={downloadCSV}
            onAddMarker={handleAddMarker}
          />
        </div>
        <div className='lg:w-9/12 w-full  max-h-[calc(100vh-100px)]'>
          <div className='map'>
            <MapContainerWrapper
              zoomLevel={zoomLevel}
              setZoomLevel={setZoomLevel}
              selectedLayer={selectedLayer}
              handleLayerChange={handleLayerChange}
              baseLayers={baseLayers}
              mapRef={mapRef}
              setMapRef={setMapRef}
              isMapClickable={isMapClickable}
              toggleMapClick={toggleMapClick}
              isClicked={isClicked}
              handleClick={handleClick}
              showTiff={showTiff}
              tiffType={tiffType}
              startDate={startDate}
              pointData={pointData}
              pointData1={pointData1}
              pointData2={pointData2}
              pointToLayer={geoJsonLayerHandlers.pointToLayer}
              pointToLayer2={geoJsonLayerHandlers.pointToLayer2}
              pointToLayer3={geoJsonLayerHandlers.pointToLayer3}
              onEachFeature={geoJsonLayerHandlers.onEachFeature}
              currentMarker={currentMarker}
              circleCenter={circleCenter}
              markerPosition={markerPosition}
              setMarkerPosition={setMarkerPosition}
              isClickActive={isClickActive}
              handleButtonClick={handleButtonClick}
              points={points}
              setPoints={setPoints}
              distance={distance}
              calculateDistance={calculateDistance}
              mapText={mapText}
              setDistance={setDistance}
              isAdmin={isAdmin}
              province={province}
              provinceCenters={provinceCenters}
            />
          </div>
        </div>
      </div>

      {!errorMessage && hasFetchedDataSuccessfully && (
        <>
          <div>
            {showDownloadButton && (
              <OptionCard
                setShowTiff={setShowTiff}
                onOptionChange={handleOptionChange}
                isSynoptic={isSynoptic}
                onTiffTypeChange={setTiffType}
                spiValue={spi}
                spiSeasonalValue={spiSeasonal}
              />
            )}
          </div>

          <div
            className='border border-solid'
            style={{
              width: "90%",
              borderRadius: "8px",
              marginBottom: "3%",
              margin: "0 auto",
            }}
          >
            {hourlyData && dailyData ? (
              <ChartRenderer
                key={chartKey}
                chartData={chartData}
                selectedOptions={selectedOptions}
                sunrise={sunrise}
                sunset={sunset}
                persianDates={persianDates}
                persianDatesHourly={persianDatesHourly}
                hourlyData={hourlyData}
                translatedWsData={translatedWsData}
                ws={ws}
              />
            ) : (
              <p>{mapText.loadingText}</p>
            )}
          </div>
        </>
      )}

      {distance && (
        <div
          style={{
            position: "absolute",
            bottom: "4vh",
            left: "30%",
            zIndex: 10000,
            background: "#fff",
            borderRadius: "8px",
            padding: "1% 2%",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          <p>
            {mapText.distanseText}: {distance} {mapText.meterText}
          </p>
        </div>
      )}
    </div>
  );
};

export default MapWithAddMarker;
